﻿import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog } from '@progress/kendo-react-dialogs';
import { TextBox } from '@progress/kendo-react-inputs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ChevronRightSolid, PlusOutline, SearchOutline } from '../../../../../../icons/icons';
import { NoteCategory } from '../../../../../../types/notes';
import MarkerText from '../../../../../components/inputs/MarkerText';
import PulseHighlight from '../../../../../components/inputs/PulseHighlight';
import SegmentedControl from '../../../../../components/inputs/SegmentedControl';
import { useAppSelector } from '../../../../../hooks/store-hooks';
import Notes from '../../../../home/flyouts/Notes';
import { useFormRadioValue, useFormStringValue } from '../../../../home/hooks/forms';
import { SettingsNavTitle, useSettingsPageWidth } from '../../../context/SettingsNavContext';

export const SEARCH_PARAM_NOTE_CATEGORY = 'category';
export const SEARCH_PARAM_NOTE_ITEM = 'item';

export function SettingsNotes() {
    useSettingsPageWidth('100%');
    const aircraftItemIds = useAppSelector((state) => state.notes.aircraftWithNotes);
    const airportItemIds = useAppSelector((state) => state.notes.airportsWithNotes);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState<string>('');

    const selectedCategory = useMemo(() => {
        const categoryFromUrl = searchParams.get(SEARCH_PARAM_NOTE_CATEGORY);
        const categoryNum = categoryFromUrl !== null ? Number.parseInt(categoryFromUrl) : null;
        if (categoryNum === NoteCategory.aircraft || categoryNum === NoteCategory.airport) {
            return categoryNum;
        }
        return NoteCategory.aircraft;
    }, [searchParams]);

    const selectedItemId: string | undefined = useMemo(() => {
        const itemFromUrl = searchParams.get(SEARCH_PARAM_NOTE_ITEM);
        if (itemFromUrl) {
            return itemFromUrl;
        }
        return selectedCategory === NoteCategory.aircraft ? aircraftItemIds[0] : airportItemIds[0];
    }, [searchParams, selectedCategory, airportItemIds, aircraftItemIds]);

    const displayedItemIds = useMemo(() => {
        const baseItemIds = selectedCategory === NoteCategory.aircraft ? aircraftItemIds : airportItemIds;
        let extendedItemIds = selectedItemId && !baseItemIds.includes(selectedItemId) ? [selectedItemId, ...baseItemIds] : baseItemIds;
        if (extendedItemIds.length === 0) {
            return [];
        }
        extendedItemIds = [...extendedItemIds].sort();

        if (!searchTerm) {
            return extendedItemIds;
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        return extendedItemIds.filter((itemId) => itemId.toLowerCase().includes(lowerSearchTerm));
    }, [searchTerm, selectedCategory, selectedItemId, aircraftItemIds, airportItemIds]);

    const newItemId = useFormStringValue('');
    const [showNewItemDialog, setShowNewItemDialog] = useState<boolean>(false);

    const aircraftAirport = useFormRadioValue(
        [
            { value: NoteCategory.aircraft, label: 'Aircraft' },
            { value: NoteCategory.airport, label: 'Airport' }
        ],
        selectedCategory
    );

    useEffect(() => {
        if (aircraftAirport.value !== selectedCategory) {
            aircraftAirport.onChange({ value: selectedCategory } as any);
        }
    }, [selectedCategory]);

    useEffect(() => {
        const hasParams = searchParams.has(SEARCH_PARAM_NOTE_CATEGORY) || searchParams.has(SEARCH_PARAM_NOTE_ITEM);
        if (!hasParams) {
            setSearchParams(
                (searchParams) => {
                    searchParams.set(SEARCH_PARAM_NOTE_CATEGORY, NoteCategory.aircraft.toString());
                    const firstItemId: string | undefined = aircraftItemIds[0];
                    if (firstItemId) {
                        searchParams.set(SEARCH_PARAM_NOTE_ITEM, firstItemId);
                    }
                    return searchParams;
                },
                { replace: true }
            );
        }
    }, []);

    const handleCategoryChange = (category: NoteCategory) => {
        const firstItemId: string | undefined = category === NoteCategory.aircraft ? aircraftItemIds[0] : airportItemIds[0];
        setSearchParams(
            (searchParams) => {
                searchParams.set(SEARCH_PARAM_NOTE_CATEGORY, category.toString());
                if (firstItemId) {
                    searchParams.set(SEARCH_PARAM_NOTE_ITEM, firstItemId);
                } else {
                    searchParams.delete(SEARCH_PARAM_NOTE_ITEM);
                }
                return searchParams;
            },
            { replace: true }
        );
    };

    const handleSelectItem = useCallback(
        (itemId: string) => {
            setSearchParams(
                (searchParams) => {
                    searchParams.set(SEARCH_PARAM_NOTE_ITEM, itemId);
                    return searchParams;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const isNewItemDuplicate = useMemo(() => {
        const itemId = newItemId.value.trim().toUpperCase();
        const existingItemIds = selectedCategory === NoteCategory.aircraft ? aircraftItemIds : airportItemIds;
        return existingItemIds.includes(itemId);
    }, [newItemId.value, selectedCategory]);

    const createNewItemId = () => {
        const itemId = newItemId.value.trim().toUpperCase();
        setSearchParams(
            (searchParams) => {
                searchParams.set(SEARCH_PARAM_NOTE_ITEM, itemId);
                return searchParams;
            },
            { replace: true }
        );
        closeNewItemDialog();
    };

    const closeNewItemDialog = () => {
        setShowNewItemDialog(false);
        newItemId.onChange({ value: '' } as any);
    };

    return (
        <div className={'h-100 pb-2'}>
            <div className={'k-card settings-group-content d-flex flex-row max-h-100 h-100'}>
                <SettingsNavTitle>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>Manage notes</div>
                        <SegmentedControl
                            {...aircraftAirport}
                            onChange={(e) => handleCategoryChange(e.value as NoteCategory)}></SegmentedControl>
                    </div>
                </SettingsNavTitle>
                <div
                    className={'d-flex flex-column border-bottom'}
                    style={{ borderRight: '1px solid var(--border-color)', width: 'min(20%, 280px)', minWidth: '200px' }}>
                    <div className={'p-2 d-flex flex-column gap-2'}>
                        <div className={'d-flex justify-content-between align-items-center'}>
                            <div className={'k-font-weight-medium'}>Items</div>
                            <PulseHighlight active={displayedItemIds.length === 0}>
                                <Button
                                    size={'small'}
                                    themeColor={'primary'}
                                    svgIcon={PlusOutline}
                                    title={'Add new item'}
                                    onClick={() => setShowNewItemDialog(true)}></Button>
                            </PulseHighlight>
                        </div>
                        <TextBox
                            placeholder="Search items..."
                            className={'flex-grow-1'}
                            value={searchTerm}
                            prefix={() => (
                                <div className="d-inline-flex align-items-center ps-3">
                                    <SvgIcon icon={SearchOutline} />
                                </div>
                            )}
                            onChange={(e) => setSearchTerm(e.value as string)}></TextBox>
                    </div>
                    <div className={'flex-grow-1 overflow-auto flight-details-scrollbars border-top'}>
                        {displayedItemIds.map((itemId) => (
                            <div
                                className={
                                    'd-flex justify-content-between align-items-center p-2 px-3 clickable ' +
                                    (selectedItemId === itemId ? 'selected-light-bg' : '')
                                }
                                key={itemId}
                                onClick={() => handleSelectItem(itemId)}>
                                <div className={'d-flex justify-content-between align-items-center w-100'}>
                                    <MarkerText highlight={searchTerm}>{itemId}</MarkerText>
                                    <SvgIcon icon={ChevronRightSolid} size={'small'} className={'text-secondary'}></SvgIcon>
                                </div>
                            </div>
                        ))}
                        {displayedItemIds.length === 0 && (
                            <div className={'p-3'}>
                                <div className="d-flex flex-column align-items-center justify-content-center text-center text-secondary my-4">
                                    <div className="k-font-size-lg k-font-weight-medium mb-2">No items found.</div>
                                    <div>
                                        Add a new {selectedCategory === NoteCategory.aircraft ? 'aircraft' : 'airport'} item with{' '}
                                        <SvgIcon icon={PlusOutline} /> to start creating notes.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="d-flex flex-column flex-grow-1 w-100">
                    <Notes showHeader={true} itemId={selectedItemId ?? null} category={selectedCategory} />
                </div>

                {showNewItemDialog && (
                    <Dialog
                        title={
                            <div>
                                <div>Add a new item for notes</div>
                                <div className={'secondary-text-color small'}>
                                    Create a new {selectedCategory === NoteCategory.aircraft ? 'aircraft' : 'airport'} entry
                                </div>
                            </div>
                        }
                        onClose={closeNewItemDialog}>
                        <div className={'d-flex flex-column gap-3 mt-3'} style={{ minWidth: '470px' }}>
                            <div className={'d-flex flex-column flex-grow-1'}>
                                <div>{selectedCategory === NoteCategory.aircraft ? 'Aircraft ID or tail number' : 'Airport ICAO code'}</div>
                                <TextBox
                                    placeholder={`e.g. ${selectedCategory === NoteCategory.aircraft ? 'AAL123' : 'KJFK'}`}
                                    {...newItemId}></TextBox>
                                {isNewItemDuplicate && (
                                    <span className={'text-danger small mt-1'} style={{ fontSize: '.75em' }}>
                                        This item already exists.{' '}
                                        <span
                                            className="clickable text-primary mt-1"
                                            onClick={() => {
                                                handleSelectItem(newItemId.value.trim().toUpperCase());
                                                closeNewItemDialog();
                                            }}>
                                            Click here to select it.
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <Button onClick={closeNewItemDialog}>Cancel</Button>
                                <Button
                                    themeColor={'primary'}
                                    disabled={isNewItemDuplicate || !newItemId.value.trim()}
                                    onClick={createNewItemId}>
                                    Create
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                )}
            </div>
        </div>
    );
}
